import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { validateOngKey } from '../../../api/onglaye.api';
import backgroundImage from '../../../assets/background.jpg';
import { useCheckoutSession, useStartFaceScan } from '../api/hooks';
import {
  clearRegistrationContext,
  loadRegistrationContext,
  saveRegistrationContext
} from '../flow/registrationContext';
import type { RegistrationContext } from '../flow/types';
import CaptureRules from './components/CaptureRules';
import { DevBypassBanner } from './dev/DevBypassBanner';
import { devBypassFaceIndex, isDevBypassEnabled } from './dev/devBypass';
import Footer from './components/Footer';
import RegisterPageBottomSection from './components/RegisterPageBottomSection';
import RegisterPageTopSection from './components/RegisterPageTopSection';

// OngLaye frontend URL for session expired redirect
const ONGLAYE_EXPIRED_URL =
  process.env.REACT_APP_ONGLAYE_URL || 'https://onglaye.dev.ayeface.com';

// Checkout frontend URL for session expired redirect
const CHECKOUT_EXPIRED_URL =
  process.env.REACT_APP_CHECKOUT_URL || 'https://checkout.dev.ayeface.com';

/** The partner session a webview visitor arrived with, while it is still live. */
const liveExternalSessionId = (): string | null => {
  const expiresAt = sessionStorage.getItem('external_session_expires_at');
  if (!expiresAt || new Date(expiresAt).getTime() <= Date.now()) return null;
  return sessionStorage.getItem('external_session_id') || null;
};

const isSameSession = (
  stored: RegistrationContext,
  params: URLSearchParams
): boolean => {
  const ongKey = params.get('ong_key');
  if (ongKey) return stored.entry === 'onglaye' && stored.ongKey === ongKey;
  const sessionId = params.get('checkout_session_id');
  if (sessionId) return stored.checkout?.sessionId === sessionId;
  return stored.entry === 'direct';
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A visitor who already has an account for this same session goes back
  // into the flow, never to a second account form. Anything else stored is
  // from another session and is dropped.
  const [resume] = useState(() => {
    const stored = loadRegistrationContext();
    if (!stored) return false;
    if (stored.account && isSameSession(stored, searchParams)) return true;
    clearRegistrationContext();
    return false;
  });
  const [showDirectRules, setShowDirectRules] = useState(false);
  const startFaceScan = useStartFaceScan();

  const ong_key = resume ? null : searchParams.get('ong_key');
  const checkout_session_id =
    resume || ong_key ? null : searchParams.get('checkout_session_id');

  const {
    data: ongKeyValidation,
    isLoading: isValidatingOngKey,
    isError: ongKeyError
  } = useQuery({
    queryKey: ['onglaye-validate', ong_key ?? ''],
    queryFn: () => validateOngKey(ong_key!),
    enabled: !!ong_key,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  const checkoutSession = useCheckoutSession(checkout_session_id);

  const ongKeyValid =
    !!ongKeyValidation?.success && !!ongKeyValidation.bizToken;
  const ongKeyInvalid =
    !!ong_key && (ongKeyError || (!!ongKeyValidation && !ongKeyValid));
  const checkoutInvalid =
    !!checkout_session_id &&
    (checkoutSession.isError ||
      (checkoutSession.isSuccess && !checkoutSession.data.bizToken));

  useEffect(() => {
    if (ongKeyInvalid) {
      console.error('OngLaye ong_key validation failed');
      window.location.href = `${ONGLAYE_EXPIRED_URL}/expired`;
    }
  }, [ongKeyInvalid]);

  useEffect(() => {
    if (checkoutInvalid) {
      console.error('Checkout session validation failed');
      window.location.href = `${CHECKOUT_EXPIRED_URL}/expired`;
    }
  }, [checkoutInvalid]);

  if (resume) return <Navigate to="/register/payment" replace />;

  const startExternal = (context: RegistrationContext) => {
    saveRegistrationContext(context);
    navigate('/register/payment', { replace: true });
  };

  /** The context for an OngLaye or checkout entry; `faceIndex` overrides the session's own. */
  const externalContext = (faceIndex?: string): RegistrationContext | null => {
    if (ong_key && ongKeyValidation?.success && ongKeyValidation.bizToken) {
      return {
        entry: 'onglaye',
        checkout: null,
        ongKey: ong_key,
        faceIndex: faceIndex ?? ongKeyValidation.bizToken,
        prefill: null,
        externalSessionId: null,
        account: null
      };
    }
    const session = checkoutSession.data;
    if (checkout_session_id && session?.bizToken) {
      return {
        entry: session.paymentRequired
          ? 'checkoutRequired'
          : 'checkoutOptional',
        checkout: {
          sessionId: checkout_session_id,
          storeName: session.storeName,
          amount: session.amount,
          currency: session.currency
        },
        ongKey: null,
        faceIndex: faceIndex ?? session.bizToken,
        prefill: session.prefill?.phone_number
          ? {
              phoneNumber: session.prefill.phone_number,
              countryCode: session.prefill.country_code.replace(/^\+/, ''),
              phoneVerificationToken: session.phoneVerificationToken ?? null
            }
          : null,
        externalSessionId: null,
        account: null
      };
    }
    return null;
  };

  const directContext = (faceIndex: string): RegistrationContext => ({
    entry: 'direct',
    checkout: null,
    ongKey: null,
    faceIndex,
    prefill: null,
    externalSessionId: liveExternalSessionId(),
    account: null
  });

  // Development only: start the resolved entry with a stand-in face index, no scan.
  const devBypass = isDevBypassEnabled();
  const skipFaceScan = () => {
    const faceIndex = devBypassFaceIndex();
    const external = externalContext(faceIndex);
    if (external) {
      startExternal(external);
      return;
    }
    if (ong_key || checkout_session_id) return;
    localStorage.removeItem('LoggedIn');
    localStorage.removeItem('token');
    startExternal(directContext(faceIndex));
  };

  const onReady = () => {
    const external = externalContext();
    if (external) {
      startExternal(external);
      return;
    }
    if (ong_key || checkout_session_id) return;

    localStorage.removeItem('LoggedIn');
    localStorage.removeItem('token');
    startFaceScan.mutate(undefined, {
      onSuccess: ({ verificationUrl, bizToken }) => {
        saveRegistrationContext(directContext(bizToken));
        window.location.href = verificationUrl;
      },
      onError: (error) => {
        console.error('Error fetching verification URL:', error);
      }
    });
  };

  // Show loading while validating OngLaye key or checkout session
  if (
    (isValidatingOngKey && ong_key) ||
    (checkoutSession.isLoading && checkout_session_id) ||
    // Hold the spinner until the expired-page redirect takes over.
    ongKeyInvalid ||
    checkoutInvalid
  ) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center bg-white"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#5CB891] border-t-transparent"></div>
          <p className="text-lg font-medium text-gray-700">
            Validating your session...
          </p>
        </div>
      </div>
    );
  }

  const entryKind = ongKeyValid
    ? 'onglaye'
    : checkoutSession.data?.bizToken
      ? 'checkout'
      : showDirectRules && !ong_key && !checkout_session_id
        ? 'direct'
        : null;

  const banner = devBypass ? <DevBypassBanner onSkip={skipFaceScan} /> : null;

  return entryKind ? (
    <>
      {banner}
      <CaptureRules
        entryKind={entryKind}
        busy={startFaceScan.isPending || startFaceScan.isSuccess}
        onReady={onReady}
      />
    </>
  ) : (
    <div
      className="flex flex-col items-center bg-white"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh'
      }}
    >
      {banner}
      <div className="flex flex-col items-center justify-center">
        <RegisterPageTopSection />
      </div>
      <RegisterPageBottomSection
        onShowCaptureRules={() => setShowDirectRules(true)}
      />
      <Footer />
    </div>
  );
}
